import {
  ContactsV2Query,
  ContactsV2QueryVariables,
  Contact,
  SortEnum,
  useGetContactsCountQuery,
  ContactSourceTypeEnum,
} from '@/types/generated/graphql';
import { useCursorPagination } from '@shared/hooks/use-cursor-pagination';
import { useEffect, useRef, useState } from 'react';
import { useDeleteBlockContacts } from './hooks/use-delete-block-contact';
import { useExportContacts } from './hooks/use-export-contacts';
import { CURSOR_CONTACTS_LIST } from '@/graphql/cursor.graphql';
import { useSearchParams } from 'react-router-dom';
import { contact_filter_key } from './hooks/use-contact-advanced-filter';
import { LocalStorageKeys } from '@shared/constants';
import { useContactUrlFilters } from './hooks/use-contact-url-filters';

const DEFAULT_SOURCE_TYPE_VALUES = [
  String([ContactSourceTypeEnum.imported, ContactSourceTypeEnum.imported]),
  String([ContactSourceTypeEnum.manual, ContactSourceTypeEnum.manual]),
];

type SearchByField = 'name' | 'email' | 'phoneNumber';

type UseContactsOptions = {
  query?: string;
  searchBy?: SearchByField | SearchByField[];
  sourceType?: ContactSourceTypeEnum[];
  excludeCountryCode?: string[];
};

export const useContacts = (options?: UseContactsOptions) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [nameSort, setNameSort] = useState<SortEnum | undefined>(SortEnum.ASC);

  const { query: urlQuery, openContactId: paramsContactId, setQuery, setOpenContactId } = useContactUrlFilters();
  const [searchBy, setSearchBy] = useState<SearchByField>('name');
  const selectedContactId = paramsContactId === 'new' ? null : +(paramsContactId ?? 0);

  const countryCodeFilter = searchParams
    .getAll(contact_filter_key.country_code)
    .map((country) => country.split(',')[0]);
  const tagsFilter = searchParams.getAll(contact_filter_key.tags).map((tag) => +tag.split(',')[0]);

  const sourceTypeParams = searchParams.getAll(contact_filter_key.source_types);
  const sourceTypeFilter = sourceTypeParams.map((sourceType) => sourceType.split(',')[0]) as ContactSourceTypeEnum[];
  const sourceTypeEmpty = !options?.sourceType && sourceTypeFilter.length === 0;

  const hasHadSourceTypeRef = useRef(false);
  useEffect(() => {
    if (sourceTypeFilter.length > 0) hasHadSourceTypeRef.current = true;
  }, [sourceTypeParams.join(',')]);

  useEffect(() => {
    if (sourceTypeEmpty && hasHadSourceTypeRef.current && paramsContactId && paramsContactId !== 'new') {
      setOpenContactId(null);
    }
  }, [sourceTypeEmpty, paramsContactId, setOpenContactId]);

  const hasSeededSourceTypeRef = useRef(false);
  useEffect(
    function markSourceTypeSeeded() {
      if (options?.sourceType || sourceTypeParams.length === 0) return;
      hasSeededSourceTypeRef.current = true;
    },
    [options?.sourceType, sourceTypeParams.join(',')]
  );

  useEffect(
    function persistSourceTypeFilter() {
      if (options?.sourceType || !hasSeededSourceTypeRef.current) return;
      localStorage.setItem(LocalStorageKeys.contactSourceTypeFilter, JSON.stringify(sourceTypeParams));
    },
    [options?.sourceType, sourceTypeParams.join(',')]
  );

  useEffect(function seedDefaultSourceTypesOnMount() {
    if (options?.sourceType) return;
    setSearchParams((prev) => {
      if (prev.getAll(contact_filter_key.source_types).length > 0) return prev;
      const persisted = localStorage.getItem(LocalStorageKeys.contactSourceTypeFilter);
      const persistedValues = persisted ? (JSON.parse(persisted) as string[]) : [];
      const valuesToSeed = persistedValues.length > 0 ? persistedValues : DEFAULT_SOURCE_TYPE_VALUES;
      const next = new URLSearchParams(prev);
      valuesToSeed.forEach((value) => next.append(contact_filter_key.source_types, value));
      return next;
    });
  }, []);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const query = options?.query ?? urlQuery;

  const contactFilterVariables = {
    query,
    countryCode: countryCodeFilter,
    tags: tagsFilter,
    sourceType: query.trim()
      ? [ContactSourceTypeEnum.imported, ContactSourceTypeEnum.manual, ContactSourceTypeEnum.system]
      : (options?.sourceType ?? sourceTypeFilter),
  };

  const searchByFields = options?.searchBy ?? searchBy;
  const selectedFields = Array.isArray(searchByFields) ? searchByFields : [searchByFields];

  const { data: contactCount } = useGetContactsCountQuery({
    variables: contactFilterVariables,
    skip: sourceTypeEmpty,
  });

  const { data, loading, initialLoading, loadNextRef, hasNextPage } = useCursorPagination<
    ContactsV2Query,
    ContactsV2QueryVariables
  >({
    schema: CURSOR_CONTACTS_LIST,
    variables: {
      order: 'name',
      sort: nameSort,
      fields: selectedFields,
      excludeCountryCode: options?.excludeCountryCode,
      ...contactFilterVariables,
    },
    responseDataKey: 'contactsV2',
    options: { skip: sourceTypeEmpty },
  });

  function setFilter(key: string, value: string) {
    if (key === 'query') {
      setQuery(value);
    }
  }

  function setSearchByField(value: SearchByField) {
    setSearchBy(value);
  }

  function handleNameSort() {
    setNameSort((prev) => (prev === undefined ? SortEnum.ASC : prev === SortEnum.ASC ? SortEnum.DESC : undefined));
  }

  const deleteModal = useDeleteBlockContacts({ selectedRowKeys, setSelectedRowKeys });

  const { exportContacts, exportLoading } = useExportContacts();

  const handleRowClick = (contact: Contact) => {
    setOpenContactId(contact?.id + '');
  };

  return {
    contactList: {
      data,
      loading,
      initialLoading,
      setFilter,
      loadNextRef,
      hasNextPage,
      rowSelection,
      handleRowClick,
      selectedContactId,
      // Open add drawer and ensure update drawer is closed
      openDrawer: !!paramsContactId,
      showDrawer: () => {
        setOpenContactId('new');
      },
      onClose: () => {
        setOpenContactId(null);
      },
      ...deleteModal,
      contactCount,
      exportContacts: () =>
        exportContacts({
          selectedRowKeys,
          setSelectedRowKeys,
        }),
      exportLoading: exportLoading,
      searchBy,
      setSearchBy: setSearchByField,
      nameSort,
      handleNameSort,
      noFilterApplied: sourceTypeEmpty,
    },
  };
};
